import { Button } from '@thanx/uikit/button'
import imgThanxAppIcon from 'assets/images/integrations/app-icons/img_thanx.png'
import imgFeedbackPhone from 'assets/images/order-feedback/img_feedback_phone.png'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { feedbackProviderName } from '../feedbackOwnership'
import PromoBlock from './PromoBlock'
import { providerAppIcon } from './providerAppIcons'
import type { OverviewContent } from './resolveOverviewState'
import {
  OVERVIEW_CARD_BORDER,
  OVERVIEW_SECTION_BODY_OFFSET,
  OVERVIEW_SECTION_HEADING_STYLE,
  OVERVIEW_SETUP_CARD_PADDING_X,
  OVERVIEW_SETUP_CARD_PADDING_Y,
  OVERVIEW_TEXT_LINK_STYLE,
} from './sectionLayout'

const t = buildTranslate('order_feedback')

// The illustration stage beside the body copy, and the phone frame standing
// on it. Measured from the prototype; none of these has a palette equivalent.
const ILLUSTRATION_BACKGROUND = 'linear-gradient(158deg, #effafa, #dbefef)'
const ILLUSTRATION_RING = 'inset 0 0 0 1px rgba(20, 28, 38, 0.05)'
const PHONE_BEZEL = '#232c37'
const PHONE_SHADOW = '0 10px 24px rgba(20, 28, 38, 0.28)'

type Props = {
  content: OverviewContent
  onCta?: () => void
  onPromoCta?: () => void
}

// The "not yet collecting" mark: a receipt with nothing written on it.
// Copied from the prototype rather than redrawn, and decorative — the card's
// title says the same thing in words.
const NotCollectingMark = () => (
  <svg
    width={26}
    height={26}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M8 2V6M12 2V6M16 2V6M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V8M20 12V14M20 18V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H17M13 22H11M7 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V18M4 14V12M4 8V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8M8 10H14M8 14H16M8 18H13" />
  </svg>
)

// The power symbol the design leads its "Turn on feedback" button with, also
// copied from the prototype. Decorative — the button's own label is the
// accessible name.
const PowerMark = () => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 2v10" />
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
  </svg>
)

// The "Your feedback setup" section: its heading, the setup card or status
// banner beneath it, and the RecoveryAI promo. Renders nothing when the state
// doesn't need the section at all (rai_thanx, rai_all — RecoveryAI already
// covers every digital order).
//
// context:
//   The promo renders from here rather than as a sibling because the design
//   does not give it a card of its own in every state. Where Thanx is (or
//   would be) collecting, it is an inset band INSIDE this card, bled to that
//   card's edges and eating its bottom padding, so the two read as one card
//   with a tinted foot. Only where a third-party tool collects is the promo a
//   separate card — and the thing above it is then a compact banner, not a
//   card at all. Rendering it as a second bordered sibling everywhere is what
//   made this section ~67px too tall in `new`, ~59px too tall in the
//   third-party states, and 45px too SHORT in `thanx`.
//
//   This card is deliberately NOT gated on the written-feedback collection
//   toggle, and must not be made so. The design gates a DIFFERENT card by
//   that toggle — the "Your current setup" card inside Settings › Feedback
//   collection, which sits below the toggle, appears only while it is on, and
//   moves only on Publish rather than on the toggle's own change. The card
//   here is the Overview's, and the design renders it in every state that
//   shows this section, "not collecting" included: there it reads "Not yet
//   setup" and carries the only "turn on feedback" affordance on the page.
//   Hiding it when collection is off would strand the merchant with no way
//   to switch it on. That CTA has no destination until the collection toggle
//   itself ships, which is why the Overview leaves this state's handler
//   undefined rather than pointing it somewhere guessed.
const FeedbackSetupSection = ({ content, onCta, onPromoCta }: Props) => {
  const [css] = useStyletron()
  const { feedbackSetup, thirdPartyProvider } = content

  if (!feedbackSetup.visible) return null

  const providerName = feedbackProviderName(thirdPartyProvider)
  const appIcon = providerAppIcon(thirdPartyProvider)

  const isIllustrated = feedbackSetup.variant === 'illustrated'

  // Heading and body are flex children so their margins cannot collapse into
  // one another: the design's 38px gap lives entirely on the body, and the
  // heading's own bottom margin is zeroed rather than left to be swallowed.
  // That margin exists to space a heading from a sub-line beneath it, which
  // this section doesn't have; keeping both live would leave the rendered gap
  // as whichever margin happens to be larger.
  const section = css({ display: 'flex', flexDirection: 'column' })
  const body = css({ marginTop: OVERVIEW_SECTION_BODY_OFFSET })

  const heading = (
    <h2 className={css({ ...OVERVIEW_SECTION_HEADING_STYLE, margin: 0 })}>
      {t('feedback_setup_heading')}
    </h2>
  )

  // The 24px line-height on the copy blocks is load-bearing rather than
  // inherited decoration: the all-caps label is an inline box, so the height
  // of the line it sits on comes from this block's own strut, not from the
  // label's smaller 16.5px. Dropping it shortens every card with a label.
  const copyBlock = { lineHeight: '24px' }

  const copy = (
    <>
      {feedbackSetup.labelKey && (
        <span
          className={css({
            fontSize: '11px',
            fontWeight: 800,
            lineHeight: '16.5px',
            letterSpacing: '0.55px',
            textTransform: 'uppercase',
            color: palette.grey70,
          })}
        >
          {t(feedbackSetup.labelKey)}
        </span>
      )}
      {feedbackSetup.bodyKey && (
        <p
          className={css({
            fontSize: '15px',
            lineHeight: '24px',
            color: palette.grey70,
            margin: '8px 0 0',
          })}
        >
          {t(feedbackSetup.bodyKey, { provider: providerName })}
        </p>
      )}
    </>
  )

  if (feedbackSetup.variant === 'compact') {
    return (
      <div className={section}>
        {heading}
        <div className={body}>
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '18px 22px',
              margin: '0 0 16px',
              backgroundColor: palette.white,
              border: `1px solid ${palette.grey20}`,
              borderRadius: '12px',
            })}
          >
            {appIcon && providerName && (
              <div
                className={css({
                  flexShrink: 0,
                  width: '42px',
                  height: '42px',
                  borderRadius: '10px',
                  backgroundColor: palette.grey10,
                  overflow: 'hidden',
                })}
              >
                <img
                  src={appIcon}
                  alt={providerName}
                  className={css({
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  })}
                />
              </div>
            )}
            <div className={css({ flex: '1 1 0%', minWidth: 0 })}>
              {feedbackSetup.statusKey && providerName && (
                <div
                  className={css({
                    fontSize: '16px',
                    fontWeight: 700,
                    lineHeight: '24px',
                    color: palette.grey90,
                  })}
                >
                  {t(feedbackSetup.statusKey, { provider: providerName })}
                </div>
              )}
              {feedbackSetup.bodyKey && (
                <p
                  className={css({
                    fontSize: '13.5px',
                    lineHeight: '20.25px',
                    color: palette.grey70,
                    margin: '3px 0 0',
                  })}
                >
                  {t(feedbackSetup.bodyKey, { provider: providerName })}
                </p>
              )}
            </div>
          </div>

          <PromoBlock
            content={content}
            variant="standalone"
            onCta={onPromoCta}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={section}>
      {heading}
      <div className={body}>
        <div
          className={css({
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            padding: `${OVERVIEW_SETUP_CARD_PADDING_Y}px ${OVERVIEW_SETUP_CARD_PADDING_X}px`,
            backgroundColor: palette.white,
            border: `1.5px solid ${OVERVIEW_CARD_BORDER}`,
            borderRadius: '14px',
          })}
        >
          {feedbackSetup.badgeKey && (
            // The badge straddles the card's top border rather than sitting
            // inside it, so it is taken out of flow — left in flow it adds its
            // own height to the card, which is where 36px of this section's
            // old overshoot came from.
            <div
              className={css({
                position: 'absolute',
                top: '-12px',
                left: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0 20px',
                zIndex: 2,
                pointerEvents: 'none',
              })}
            >
              <span
                className={css({
                  pointerEvents: 'auto',
                  whiteSpace: 'nowrap',
                  fontSize: '10.5px',
                  fontWeight: 800,
                  lineHeight: 1,
                  letterSpacing: '0.42px',
                  textTransform: 'uppercase',
                  color: palette.grey90,
                  backgroundColor: palette.grey20,
                  border: `1px solid ${palette.grey30}`,
                  padding: '5px 11px',
                  borderRadius: '20px',
                })}
              >
                {t(feedbackSetup.badgeKey)}
              </span>
            </div>
          )}

          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            })}
          >
            {/* On the illustrated card this tile is the Thanx app icon, set as
                a cover background the way the design does rather than as an
                <img>: the icon's own artwork is a full-bleed spearmint
                rounded square, so it fills the tile exactly and the tint
                beneath it only ever shows while the file is still loading.
                `role`/`aria-label` give it the accessible name the element
                would otherwise lack. The stacked card keeps the grey tile and
                the inline receipt mark, which needs no asset. */}
            <span
              role={isIllustrated ? 'img' : undefined}
              aria-label={isIllustrated ? t('thanx_logo_alt') : undefined}
              className={css({
                flexShrink: 0,
                width: '52px',
                height: '52px',
                borderRadius: '13px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                backgroundColor: isIllustrated
                  ? palette.spearmint
                  : palette.grey10,
                color: palette.grey70,
                ...(isIllustrated && {
                  backgroundImage: `url(${imgThanxAppIcon})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }),
              })}
            >
              {!isIllustrated && <NotCollectingMark />}
            </span>
            {feedbackSetup.titleKey && (
              <h3
                className={css({
                  fontSize: '22px',
                  fontWeight: 800,
                  lineHeight: 1.2,
                  letterSpacing: '-0.3px',
                  color: palette.grey90,
                  margin: '3px 0 0',
                })}
              >
                {t(feedbackSetup.titleKey)}
              </h3>
            )}
          </div>

          {isIllustrated ? (
            <>
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '24px',
                  marginTop: '14px',
                })}
              >
                {/* The phone standing on the tinted stage, showing the guest's
                    own feedback form. The stage has a fixed height and clips,
                    so the screenshot runs off its bottom edge exactly as the
                    design has it — the image's own height is deliberately not
                    what sizes this row. Decorative: the label and body beside
                    it already say what the guest sees. */}
                <div
                  className={css({
                    flexShrink: 0,
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    width: '162px',
                    height: '138px',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    backgroundImage: ILLUSTRATION_BACKGROUND,
                    boxShadow: ILLUSTRATION_RING,
                  })}
                >
                  <div
                    className={css({
                      flexShrink: 0,
                      width: '142px',
                      marginTop: '16px',
                      border: `6px solid ${PHONE_BEZEL}`,
                      borderBottom: 'none',
                      borderRadius: '24px 24px 0 0',
                      overflow: 'hidden',
                      backgroundColor: palette.white,
                      boxShadow: PHONE_SHADOW,
                    })}
                  >
                    <img
                      src={imgFeedbackPhone}
                      alt=""
                      className={css({ width: '100%', display: 'block' })}
                    />
                  </div>
                </div>

                <div
                  className={css({
                    ...copyBlock,
                    flex: '1 1 0%',
                    minWidth: 0,
                    marginBottom: '22px',
                  })}
                >
                  {copy}
                </div>
              </div>

              {feedbackSetup.ctaKey && (
                <div
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                    gap: '24px',
                    marginTop: '18px',
                  })}
                >
                  <button
                    type="button"
                    className={css(OVERVIEW_TEXT_LINK_STYLE)}
                    onClick={() => onCta?.()}
                  >
                    {t(feedbackSetup.ctaKey)}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div
              className={css({
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '24px',
                marginTop: '8px',
                paddingTop: '14px',
                borderTop: `1px solid ${palette.grey10}`,
              })}
            >
              <div
                className={css({ ...copyBlock, flex: '1 1 0%', minWidth: 0 })}
              >
                {copy}
              </div>
              {feedbackSetup.ctaKey && (
                <Button
                  kind="secondary"
                  startEnhancer={() => <PowerMark />}
                  onClick={() => onCta?.()}
                >
                  {t(feedbackSetup.ctaKey)}
                </Button>
              )}
            </div>
          )}

          <PromoBlock content={content} variant="inset" onCta={onPromoCta} />
        </div>
      </div>
    </div>
  )
}

export default FeedbackSetupSection
